import React, { useEffect, useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { navigableProps } from '../utils/navigation';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
  CircularProgress,
  Alert,
  Link as MuiLink,
  Stack,
  Typography,
} from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import PageHeader from '../components/common/PageHeader';
import TableSkeleton from '../components/common/TableSkeleton';
import { MitarbeiterKurzprofilLink } from '../components/common/MitarbeiterKurzprofilLink';
import { mitarbeiterService } from '../services/mitarbeiterService';
import { benachrichtigungenService } from '../services/benachrichtigungenService';
import { usePermissions } from '../hooks/usePermissions';
import { useNotification } from '../hooks/NotificationContext';
import * as P from '../constants/permissions';
import { MitarbeiterList } from '../types';

const MitarbeiterListPage: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { showSuccess, showError } = useNotification();
  const canPush = hasPermission(P.VERWALTUNG_MANAGE) || hasPermission(P.MITARBEITER_EDIT);
  const [mitarbeiter, setMitarbeiter] = useState<MitarbeiterList[]>([]);
  const [loading, setLoading] = useState(true);

  const [pushDialogOpen, setPushDialogOpen] = useState(false);
  const [mitarbeiterWithGotify, setMitarbeiterWithGotify] = useState<{ id: number; vorname: string; nachname: string }[]>([]);
  const [pushDialogLoading, setPushDialogLoading] = useState(false);
  const [pushSelectedIds, setPushSelectedIds] = useState<number[]>([]);
  const [pushTitle, setPushTitle] = useState('');
  const [pushMessage, setPushMessage] = useState('');
  const [pushSending, setPushSending] = useState(false);

  useEffect(() => {
    const loadMitarbeiter = async () => {
      try {
        setLoading(true);
        const data = await mitarbeiterService.list();
        setMitarbeiter(data);
      } catch (error) {
        console.error('Fehler beim Laden der Mitarbeiter:', error);
      } finally {
        setLoading(false);
      }
    };

    loadMitarbeiter();
  }, []);

  const handleCreateClick = () => {
    navigate('/mitarbeiter/neu');
  };

  const handleOpenPushDialog = async () => {
    setPushDialogOpen(true);
    setPushSelectedIds([]);
    setPushTitle('');
    setPushMessage('');
    setPushDialogLoading(true);
    try {
      const data = await benachrichtigungenService.getMitarbeiterWithGotify();
      setMitarbeiterWithGotify(data);
    } catch {
      showError('Fehler beim Laden der Mitarbeiter mit Gotify');
    } finally {
      setPushDialogLoading(false);
    }
  };

  const handleSendPush = async () => {
    if (pushSelectedIds.length === 0 || !pushTitle.trim() || !pushMessage.trim()) {
      showError('Bitte Empfänger, Titel und Nachricht ausfüllen');
      return;
    }
    setPushSending(true);
    try {
      const result = await benachrichtigungenService.sendPush({
        mitarbeiter_ids: pushSelectedIds,
        title: pushTitle.trim(),
        message: pushMessage.trim(),
      });
      setPushDialogOpen(false);
      if (result.uebersprungen > 0) {
        const nic = result.uebersprungen_ohne_gotify_konfiguration ?? 0;
        const pol = result.uebersprungen_versand_unterdrueckt ?? 0;
        const parts: string[] = [];
        if (nic > 0) {
          parts.push(`${nic} ohne Gotify-Konfiguration (Token/URL)`);
        }
        if (pol > 0) {
          parts.push(
            `${pol} durch Versand-Policy übersprungen (z. B. GOTIFY_DELIVERY_MODE=suppress auf Nicht-Prod)`
          );
        }
        if (parts.length === 0) {
          parts.push(`${result.uebersprungen} übersprungen`);
        }
        showSuccess(`Push: ${result.gesendet} zugestellt. ${parts.join('. ')}.`);
      } else if (result.fehlgeschlagen > 0) {
        showSuccess(`Push gesendet: ${result.gesendet} erfolgreich, ${result.fehlgeschlagen} fehlgeschlagen.`);
      } else {
        showSuccess(`Push gesendet an ${result.gesendet} Mitarbeiter.`);
      }
    } catch {
      showError('Fehler beim Senden von Push-Nachrichten');
    } finally {
      setPushSending(false);
    }
  };

  if (loading) {
    return <TableSkeleton columns={5} rows={6} />;
  }

  return (
    <Box>
      <PageHeader
        title="Mitarbeiter"
        breadcrumbs={[{ label: 'Mitarbeiter' }]}
        onAdd={handleCreateClick}
        addLabel="Neu erstellen"
        actions={
          canPush ? (
            <Button
              variant="outlined"
              startIcon={<NotificationsActiveIcon />}
              onClick={handleOpenPushDialog}
            >
              Push senden
            </Button>
          ) : undefined
        }
      />
      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>E-Mail</TableCell>
              <TableCell>Rolle</TableCell>
              <TableCell>Aktiv</TableCell>
              <TableCell>Profil</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {mitarbeiter.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  Keine Mitarbeiter gefunden
                </TableCell>
              </TableRow>
            ) : (
              mitarbeiter.map((mitarbeiter) => (
                <TableRow
                  key={mitarbeiter.id}
                  hover
                  {...navigableProps(navigate, `/mitarbeiter/${mitarbeiter.id}`)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    {mitarbeiter.vorname} {mitarbeiter.nachname}
                  </TableCell>
                  <TableCell>{mitarbeiter.email}</TableCell>
                  <TableCell>{mitarbeiter.rollen?.map(r => r.name).join(', ') || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={mitarbeiter.aktiv ? 'Aktiv' : 'Inaktiv'}
                      color={mitarbeiter.aktiv ? 'success' : 'error'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <MuiLink
                        component={RouterLink}
                        to={`/mitarbeiter/${mitarbeiter.id}`}
                        underline="hover"
                        color="primary"
                        variant="body2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Details
                      </MuiLink>
                      <Typography component="span" variant="body2" color="text.secondary" sx={{ userSelect: 'none' }}>
                        ·
                      </Typography>
                      <MitarbeiterKurzprofilLink mitarbeiterId={mitarbeiter.id} sx={{ fontSize: '0.875rem' }}>
                        Kurzprofil
                      </MitarbeiterKurzprofilLink>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={pushDialogOpen} onClose={() => !pushSending && setPushDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Push-Nachricht senden</DialogTitle>
        <DialogContent>
          {pushDialogLoading ? (
            <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          ) : mitarbeiterWithGotify.length === 0 ? (
            <Alert severity="info" sx={{ mt: 1 }}>
              Keine Mitarbeiter mit konfiguriertem Gotify gefunden. Bitte zuerst Gotify-Token und URL in den Mitarbeiter-Einstellungen hinterlegen.
            </Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Empfänger</InputLabel>
                <Select
                  multiple
                  value={pushSelectedIds}
                  onChange={(e) => setPushSelectedIds(e.target.value as number[])}
                  input={<OutlinedInput label="Empfänger" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected as number[]).map((id) => {
                        const m = mitarbeiterWithGotify.find((x) => x.id === id);
                        return <Chip key={id} label={m ? `${m.vorname} ${m.nachname}` : id} size="small" />;
                      })}
                    </Box>
                  )}
                >
                  {mitarbeiterWithGotify.map((m) => (
                    <MenuItem key={m.id} value={m.id}>
                      <Checkbox checked={pushSelectedIds.indexOf(m.id) > -1} />
                      <ListItemText primary={`${m.vorname} ${m.nachname}`} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Titel"
                value={pushTitle}
                onChange={(e) => setPushTitle(e.target.value)}
                fullWidth
                size="small"
                placeholder="z.B. Wichtige Mitteilung"
                inputProps={{ maxLength: 255 }}
              />
              <TextField
                label="Nachricht"
                value={pushMessage}
                onChange={(e) => setPushMessage(e.target.value)}
                fullWidth
                multiline
                rows={4}
                size="small"
                placeholder="Freitext-Nachricht..."
                inputProps={{ maxLength: 10000 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPushDialogOpen(false)} disabled={pushSending}>
            Abbrechen
          </Button>
          <Button
            variant="contained"
            onClick={handleSendPush}
            disabled={
              pushSending ||
              pushDialogLoading ||
              mitarbeiterWithGotify.length === 0 ||
              pushSelectedIds.length === 0 ||
              !pushTitle.trim() ||
              !pushMessage.trim()
            }
          >
            {pushSending ? <CircularProgress size={24} /> : 'Senden'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MitarbeiterListPage;
